// src/services/dataImportService.js
// Service for importing patient and claims data into Supabase or AWS

import { supabase } from '../lib/supabaseClient';
import * as awsApi from './awsApiService';

const useAWS = (process.env.REACT_APP_USE_AWS || '').trim() === 'true';

/**
 * Import patients into the database
 */
export const importPatients = async (patients, practiceId) => {
  const results = {
    successful: 0,
    failed: 0,
    duplicates: 0,
    failedRecords: []
  };

  for (const patient of patients) {
    try {
      // Add practice_id to patient data
      const patientData = {
        ...patient,
        practice_id: practiceId
      };

      if (useAWS) {
        // Use AWS API for import
        const response = await awsApi.createPatient(patientData);

        if (response.success) {
          results.successful++;
        } else if (response.error?.includes('duplicate') || response.error?.includes('already exists')) {
          results.duplicates++;
          results.failedRecords.push(
            `Duplicate: ${patient.first_name} ${patient.last_name} (DOB: ${patient.date_of_birth})`
          );
        } else {
          throw new Error(response.error || 'Failed to create patient');
        }
      } else {
        // Fallback to Supabase
        // Check for duplicates (same name and DOB in same practice)
        const { data: existingPatients, error: checkError } = await supabase
          .from('patients')
          .select('id')
          .eq('practice_id', practiceId)
          .eq('first_name', patient.first_name)
          .eq('last_name', patient.last_name)
          .eq('date_of_birth', patient.date_of_birth);

        if (checkError) throw checkError;

        if (existingPatients && existingPatients.length > 0) {
          results.duplicates++;
          results.failedRecords.push(
            `Duplicate: ${patient.first_name} ${patient.last_name} (DOB: ${patient.date_of_birth})`
          );
          continue;
        }

        // Insert patient
        const { error: insertError } = await supabase
          .from('patients')
          .insert([patientData]);

        if (insertError) throw insertError;

        results.successful++;
      }

    } catch (error) {
      console.error('Error importing patient:', error);
      results.failed++;
      results.failedRecords.push(
        `${patient.first_name} ${patient.last_name}: ${error.message}`
      );
    }
  }

  return results;
};

/**
 * Import claims into the database
 */
export const importClaims = async (claims, practiceId) => {
  const results = {
    successful: 0,
    failed: 0,
    duplicates: 0,
    missingPatients: 0,
    failedRecords: []
  };

  for (const claim of claims) {
    try {
      if (useAWS) {
        // Use AWS API for import
        const claimData = {
          ...claim,
          practice_id: practiceId
        };

        const response = await awsApi.createClaim(claimData);

        if (response.success) {
          results.successful++;
        } else if (response.error?.includes('duplicate') || response.error?.includes('already exists')) {
          results.duplicates++;
          results.failedRecords.push(
            `Duplicate claim: ${claim.open_dental_claim_id || 'unknown'}`
          );
        } else if (response.error?.includes('patient') || response.error?.includes('not found')) {
          results.missingPatients++;
          results.failed++;
          results.failedRecords.push(
            `Patient ID ${claim.patient_id} not found in practice`
          );
        } else {
          throw new Error(response.error || 'Failed to create claim');
        }
      } else {
        // Fallback to Supabase
        // Verify patient exists in the practice (try UUID first, then PMS ID)
        let patientData = null;
        let patientError = null;

        // First try: match by FlowRCM UUID
        const { data: uuidMatch, error: uuidError } = await supabase
          .from('patients')
          .select('id')
          .eq('id', claim.patient_id)
          .eq('practice_id', practiceId)
          .single();

        if (uuidMatch) {
          patientData = uuidMatch;
        } else {
          // Second try: match by PMS patient ID (for real-world imports)
          const { data: pmsMatch, error: pmsError } = await supabase
            .from('patients')
            .select('id')
            .eq('open_dental_patient_id', claim.patient_id)
            .eq('practice_id', practiceId)
            .single();

          if (pmsMatch) {
            patientData = pmsMatch;
          } else {
            patientError = pmsError || uuidError;
          }
        }

        if (patientError || !patientData) {
          results.missingPatients++;
          results.failed++;
          results.failedRecords.push(
            `Patient ID ${claim.patient_id} not found in practice`
          );
          continue;
        }

        // Check for duplicate claims (same open_dental_claim_id)
        if (claim.open_dental_claim_id) {
          const { data: existingClaims, error: checkError } = await supabase
            .from('claims')
            .select('id')
            .eq('practice_id', practiceId)
            .eq('open_dental_claim_id', claim.open_dental_claim_id);

          if (checkError) throw checkError;

          if (existingClaims && existingClaims.length > 0) {
            results.duplicates++;
            results.failedRecords.push(
              `Duplicate claim: ${claim.open_dental_claim_id}`
            );
            continue;
          }
        }

        // Add practice_id and use the real patient UUID
        const claimData = {
          ...claim,
          patient_id: patientData.id,  // Use the actual UUID from patients table
          practice_id: practiceId
        };

        // Insert claim
        const { error: insertError } = await supabase
          .from('claims')
          .insert([claimData]);

        if (insertError) throw insertError;

        results.successful++;
      }

    } catch (error) {
      console.error('Error importing claim:', error);
      results.failed++;
      results.failedRecords.push(
        `Claim for patient ${claim.patient_id}: ${error.message}`
      );
    }
  }

  return results;
};

/**
 * Get import history for a practice
 */
export const getImportHistory = async (practiceId, limit = 20) => {
  try {
    if (useAWS) {
      // AWS mode: use API to get audit logs
      const result = await awsApi.getAuditLogs(practiceId, { limit });
      if (result.success && result.logs) {
        // Filter for import actions
        return result.logs.filter(log =>
          log.action === 'csv_import_patients' || log.action === 'csv_import_claims'
        );
      }
      return [];
    }

    // Supabase mode
    const { data, error } = await supabase
      .from('audit_logs')
      .select('*')
      .eq('practice_id', practiceId)
      .in('action', ['csv_import_patients', 'csv_import_claims'])
      .order('created_at', { ascending: false })
      .limit(limit);

    if (error) throw error;

    return data || [];
  } catch (error) {
    console.error('Error fetching import history:', error);
    return [];
  }
};

/**
 * Log import activity to audit_logs
 */
export const logImportActivity = async (practiceId, userId, importType, results) => {
  try {
    const logData = {
      action: `csv_import_${importType}`,
      resource_type: importType,
      metadata: {
        successful: results.successful,
        failed: results.failed,
        duplicates: results.duplicates,
        timestamp: new Date().toISOString()
      }
    };

    if (useAWS) {
      // AWS mode: use API to create audit log
      await awsApi.createAuditLog(logData);
      return;
    }

    // Supabase mode
    const supabaseLogData = {
      ...logData,
      practice_id: practiceId,
      user_id: userId
    };

    const { error } = await supabase
      .from('audit_logs')
      .insert([supabaseLogData]);

    if (error) throw error;

  } catch (error) {
    console.error('Error logging import activity:', error);
  }
};

/**
 * Validate patient exists before allowing claim import
 */
export const validatePatientExists = async (patientId, practiceId) => {
  try {
    if (useAWS) {
      // AWS mode: use API to get patient
      const result = await awsApi.getPatientById(patientId, practiceId);
      if (result.success && result.data) {
        return { exists: true, patient: result.data };
      }
      return { exists: false, patient: null };
    }

    // Supabase mode
    const { data, error } = await supabase
      .from('patients')
      .select('id, first_name, last_name')
      .eq('id', patientId)
      .eq('practice_id', practiceId)
      .single();

    if (error || !data) {
      return { exists: false, patient: null };
    }

    return { exists: true, patient: data };
  } catch (error) {
    console.error('Error validating patient:', error);
    return { exists: false, patient: null };
  }
};

/**
 * Batch import patients (more efficient for large datasets)
 */
export const batchImportPatients = async (patients, practiceId, batchSize = 50) => {
  // For AWS, use individual imports since we need duplicate checking per record
  // For Supabase, we can try batch inserts with fallback
  if (useAWS) {
    return importPatients(patients, practiceId);
  }

  const results = {
    successful: 0,
    failed: 0,
    duplicates: 0,
    failedRecords: []
  };

  // Split into batches
  for (let i = 0; i < patients.length; i += batchSize) {
    const batch = patients.slice(i, i + batchSize);

    // Add practice_id to all records in batch
    const batchData = batch.map(patient => ({
      ...patient,
      practice_id: practiceId
    }));

    try {
      // Insert batch
      const { data, error } = await supabase
        .from('patients')
        .insert(batchData)
        .select();

      if (error) {
        // If batch fails, fall back to individual inserts
        const individualResults = await importPatients(batch, practiceId);
        results.successful += individualResults.successful;
        results.failed += individualResults.failed;
        results.duplicates += individualResults.duplicates;
        results.failedRecords.push(...individualResults.failedRecords);
      } else {
        results.successful += data.length;
      }

    } catch (error) {
      console.error('Batch import error:', error);
      // Fall back to individual inserts
      const individualResults = await importPatients(batch, practiceId);
      results.successful += individualResults.successful;
      results.failed += individualResults.failed;
      results.duplicates += individualResults.duplicates;
      results.failedRecords.push(...individualResults.failedRecords);
    }
  }

  return results;
};

/**
 * Batch import claims (more efficient for large datasets)
 * Note: Claims require patient validation, so we use individual imports
 * which properly resolve patient_id to UUID
 */
export const batchImportClaims = async (claims, practiceId, batchSize = 50) => {
  // Both AWS and Supabase use individual imports for claims
  // since we need patient validation per record
  return importClaims(claims, practiceId);
};